import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from 'src/app/service/auth.service';
import { Router } from '@angular/router';
import { DialogService } from 'src/app/service/dialog.service';

/**
 * Service to track user inactivity and trigger actions after a defined timeout.
 * It listens for global user activity such as mouse movements, keyboard events,
 * touch interactions, and scrolling to reset the inactivity timer.
 *
 * The service emits an observable event when the inactivity timeout is reached,
 * enabling features like automatic logout or session termination.
 *
 * Usage:
 * - Start tracking inactivity using the `start` method.
 * - Stop tracking by calling the `stop` method.
 * - Subscribe to `inactivityReached$` to handle inactivity timeout events.
 */
@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly document = inject(DOCUMENT);
  /** Emits once when inactivity limit is reached. */
  private readonly inactivityReachedSubject = new Subject<void>();
  /** Global activity listeners (mouse, keyboard, touch, scroll). */
  private activitySubscription: Subscription | null = null;
  /** Single-shot inactivity timer subscription. */
  private inactivitySubscription: Subscription | null = null;

  private authSvc = inject(AuthService);
  private router = inject(Router);
  private dialogSvc = inject(DialogService);

  constructor(private readonly ngZone: NgZone) {
    this.inactivityReached$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.authSvc.logout();
      this.dialogSvc.showDialog.set(false);
      void this.router.navigate(['/']);
    });
  }

  /**
   * Emits a void observable when inactivity is detected.
   *
   * This observable can be subscribed to in order to perform actions
   * when the inactivity threshold is reached.
   *
   * @return {Observable<void>} An observable that emits when inactivity is triggered.
   */
  get inactivityReached$(): Observable<void> {
    return this.inactivityReachedSubject.asObservable();
  }

  /**
   * Starts the activity monitoring and resets the inactivity timer.
   * This method initializes the necessary processes to track user activity
   * and manage timeouts for inactivity.
   *
   * @return {void} Does not return a value.
   */
  start(): void {
    this.startActivityMonitoring();
    this.restartInactivityTimer();
  }

  /**
   * Stops the inactivity timer and activity monitoring processes.
   *
   * @return {void} This method does not return a value.
   */
  stop(): void {
    this.stopInactivityTimer();
    this.stopActivityMonitoring();
  }

  /**
   * Initiates monitoring of user activity by listening to various input events
   * such as mouse movements, key presses, and scroll actions. If activity is
   * detected, it triggers the restart of an inactivity timer. This method ensures
   * activity monitoring is executed outside the Angular zone for performance
   * optimization. If monitoring is already active, the method will return
   * immediately without redefining the subscription.
   *
   * @return {void} Does not return a value.
   */
  private startActivityMonitoring(): void {
    if (this.activitySubscription) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.activitySubscription = merge(
        fromEvent(this.document, 'mousemove'),
        fromEvent(this.document, 'mousedown'),
        fromEvent(this.document, 'keydown'),
        fromEvent(this.document, 'touchstart'),
        fromEvent(this.document, 'scroll'),
      ).subscribe(() => {
        this.restartInactivityTimer();
      });
    });
  }

  /**
   * Stops the activity monitoring by unsubscribing from the activity subscription,
   * if it currently exists. Once unsubscribed, the subscription is set to null.
   *
   * @return {void} No return value.
   */
  private stopActivityMonitoring(): void {
    if (!this.activitySubscription) {
      return;
    }

    this.activitySubscription.unsubscribe();
    this.activitySubscription = null;
  }

  /**
   * Restarts the inactivity timer to monitor user inactivity within a specified limit.
   * Stops the current inactivity timer if active and initiates a new timer
   * that triggers an inactivity event when the inactivity limit is reached.
   *
   * @return {void} This method does not return any value.
   */
  private restartInactivityTimer(): void {
    this.stopInactivityTimer();

    this.ngZone.runOutsideAngular(() => {
      this.inactivitySubscription = timer(environment.inactivityLimitMs).subscribe(() => {
        this.ngZone.run(() => {
          this.stopInactivityTimer();
          this.inactivityReachedSubject.next();
        });
      });
    });
  }

  /**
   * Stops the inactivity timer by unsubscribing from the current inactivity subscription,
   * if it exists, and clearing the reference to the subscription.
   *
   * @return {void} This method does not return any value.
   */
  private stopInactivityTimer(): void {
    if (!this.inactivitySubscription) {
      return;
    }

    this.inactivitySubscription.unsubscribe();
    this.inactivitySubscription = null;
  }
}
